import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AppBarTitleService, AppBarActionsService, AppBarAction, MessageService, EnumHelper, AppPageService, SimpleDialogService, CommonActionKey } from 'common';
import { FailureReasonService } from '../failure-reason.service';
import { FailureReasonData } from '../failure-reason.model';
import { MatDialog } from '@angular/material/dialog';
import { FormHelper, FailureReasonUsage, FailureReasonUsageLabel } from 'common';
import { UserPermissionService } from '../../../user/user-permission/user-permission.service';
import { readWrite } from '../../../user/user-permission/user-permission.data';
import { FailureReasonUpdateDialogComponent } from '../failure-reason-update-dialog/failure-reason-update-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-failure-reason-details',
    templateUrl: './failure-reason-details.component.html',
    styleUrls: ['./failure-reason-details.component.scss'],
    standalone: false
})
export class FailureReasonDetailsComponent implements OnInit {
  private saveSub: Subscription;
  private deleteSub: Subscription;

  form: UntypedFormGroup;
  usage = EnumHelper.getNamesAndValues(FailureReasonUsage).filter(u => u.value != FailureReasonUsage.All);
  data: FailureReasonDetailsComponentData;
  minExpirationDate = new Date(new Date().setDate(new Date().getDate() + 1));
  readWriteFailureReason: boolean;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly failureReasonService: FailureReasonService,
    private readonly userPermissionService: UserPermissionService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly messageService: MessageService,
    private readonly dialog: MatDialog,
    private readonly simpleDialogService: SimpleDialogService
  ) {

    this.appBarActionsService.actions = [
      { id: 'delete', label: 'Delete', disabled: true, buttonType: 'button' },
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'save', label: 'Save', buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];

    this.initForm();
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.initData.bind(this));
    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(this.actionUpdate.bind(this));

    this.userPermissionService.granted([readWrite('admin-failure-reasons')])
      .subscribe(res => {
        this.readWriteFailureReason = res;

        this.appBarActionsService.enable('save', res);
        this.appBarActionsService.enable('delete',
          this.data && this.data.failureReason && this.data.failureReason.id && res && !this.deleteSub ? true : false);
        this.appBarActionsService.enable('delete', res);

        if (res) {
          this.form.controls.usage.enable();
          this.form.controls.active.enable();
        } else {
          this.form.controls.usage.disable();
          this.form.controls.active.disable();
        }
      });
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  private actionUpdate(): void {
    this.appBarActionsService.enable('delete', this.data && this.data.failureReason && this.data.failureReason.id && this.readWriteFailureReason && !this.deleteSub ? true : false);
  }

  cancel(): void {
    this.appPageService.back();
  }

  private initData(data: FailureReasonDetailsComponentData): void {
    this.data = data;
    if (data.failureReason)
      this.form.patchValue(data.failureReason);

    this.appBarTitleService.title = data.failureReason && data.failureReason.id
      ? `Failure reason - ${data.failureReason.name}`
      : 'Create new failure reason';

    this.form.markAsPristine();
    this.actionUpdate();
  }

  delete(): void {
    if (!this.data || !this.data.failureReason || !this.data.failureReason.id)
      this.appPageService.back();

    this.simpleDialogService.openSimpleConfirmDialog(`Delete failure reason?`, "Confirm")
    .subscribe((result: CommonActionKey) => {
      if (result === CommonActionKey.Confirm) {
        this.deleteSub = this.failureReasonService.delete(this.data.failureReason.id).subscribe({
          next: () => this.appPageService.back(),
          error: this.deleteErrorHandler.bind(this)
        });
      }
      this.actionUpdate();
    });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub)
      return;

    if (!this.form.valid)
      return;

    if (!this.form.dirty) {
      this.cancel();
      return;
    }

    if (this.data && this.data.failureReason && this.data.failureReason.id) {
      FailureReasonUpdateDialogComponent.show(this.dialog, this.form, "Update").subscribe(result => {
        if (result) {
          this.saveSub = this.failureReasonService
            .update(this.data.failureReason.id, this.form.value)
            .subscribe({
              next: this.saveSuccessHandler.bind(this),
              error: this.saveErrorHandler.bind(this)
            })
        }
      });
    } else {
      FailureReasonUpdateDialogComponent.show(this.dialog, this.form, "Create").subscribe(result => {
        if (result)
          this.saveSub = this.failureReasonService
            .create(this.form.value)
            .subscribe({
              next: this.saveSuccessHandler.bind(this),
              error: this.saveErrorHandler.bind(this)
            })
      });
    }
  }

  formatUsage(usage: FailureReasonUsage): string {
    return FailureReasonUsageLabel.get(usage);
  }

  private initForm(): void {
    const notBlank = Validators.pattern(/\S/);

    this.form = this.formBuilder.group({
      id: undefined,
      code: [undefined, Validators.compose([Validators.required, notBlank, Validators.maxLength(8)])],
      externalCode: [undefined, Validators.compose([Validators.maxLength(16)])],
      name: [undefined, Validators.compose([Validators.required, notBlank, Validators.maxLength(64)])],
      description: [undefined, Validators.maxLength(65536)],
      usage: [undefined, Validators.required],
      allowToSpecifyCompetitors: [false, Validators.required],
      active: [true, Validators.required],
      createdBy: undefined,
      createdOn: undefined,
      updatedBy: undefined,
      updatedOn: undefined
    });
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private deleteErrorHandler(error: HttpErrorResponse): void {
    this.deleteSub = null;
    this.messageService.error(error);
    this.actionUpdate();
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
    this.actionUpdate();
    this.appPageService.back();
  }
}

export interface FailureReasonDetailsComponentData {
  failureReason: FailureReasonData;
}
